import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import JSZip from 'jszip';
import { calcTotalStoneWeight, DEFAULT_ANNOTATION_STYLE, DEFAULT_TEXTBOX_STYLE } from '@/utils/cad-sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Loader2, X, ChevronDown, Image, Package, Settings2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { renderViewToDataURL } from '@/lib/canvas-export';

/**
 * Renders a line between two %-based coordinates as an absolutely positioned HTML div.
 * Uses CSS transform (rotate) to draw the line at the correct angle.
 * This approach guarantees visibility in both on-screen rendering AND html-to-image export,
 * unlike SVG lines which can fail to render at small tile sizes.
 */
function HtmlLine({ x1, y1, x2, y2, color = '#000', width = 2, dashed = false, opacity = 1 }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < 0.1) return null;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  return (
    <div style={{
      position: 'absolute',
      left: `${x1}%`,
      top: `${y1}%`,
      width: `${length}%`,
      height: 0,
      borderTop: `${width}px ${dashed ? 'dashed' : 'solid'} ${color}`,
      transformOrigin: '0 0',
      transform: `rotate(${angle}deg)`,
      opacity,
      pointerEvents: 'none',
      zIndex: 5,
    }} />
  );
}

const VISIBILITY_OPTIONS = [
  { key: 'header', label: 'Header (Style, Metal, Weight)' },
  { key: 'images', label: 'Image Grid' },
  { key: 'stones', label: 'Stone Details Table' },
  { key: 'findings', label: 'Findings' },
  { key: 'finishing', label: 'Finishing' },
  { key: 'engraving', label: 'Engraving' },
  { key: 'customerImages', label: 'Customer Reference Images' },
  { key: 'notes', label: 'Notes / Other Details' },
];

export function LivePreview({ data, open, onClose }) {
  const previewRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [showVisibility, setShowVisibility] = useState(false);
  const [visibility, setVisibility] = useState(() => {
    const defaults = {};
    VISIBILITY_OPTIONS.forEach(o => { defaults[o.key] = true; });
    return defaults;
  });

  const toggleVisibility = (key) => {
    setVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalStoneWeight = calcTotalStoneWeight(data.stones);
  const totalStoneQty = data.stones.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);

  const handleDownloadConsolidated = async () => {
    if (!previewRef.current || exporting) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(previewRef.current, { pixelRatio: 3, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `${data.styleNo || 'CAD-Sheet'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!previewRef.current || exporting) return;
    setExporting(true);
    try {
      const zip = new JSZip();
      const prefix = data.styleNo || 'CAD-Sheet';
      const metadata = {
        partyDesignNo: data.customerDesignNo || '',
        styleNo: data.styleNo || '',
        pieceName: data.jewelryType || '',
      };
      for (let i = 0; i < data.images.length; i++) {
        const img = data.images[i];
        if (!img.url) continue;
        try {
          const viewDataUrl = await renderViewToDataURL(img, metadata);
          const base64 = viewDataUrl.split(',')[1];
          zip.file(`${prefix}_${img.label.replace(/[^a-zA-Z0-9]/g, '_')}.png`, base64, { base64: true });
        } catch (e) { console.error(`Failed to export view ${img.label}:`, e); }
      }
      const consolidatedUrl = await toPng(previewRef.current, { pixelRatio: 3, backgroundColor: '#ffffff' });
      const consolidatedBase64 = consolidatedUrl.split(',')[1];
      zip.file(`${prefix}_Consolidated.png`, consolidatedBase64, { base64: true });
      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `${prefix}_Views.zip`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('ZIP with all views + consolidated sheet downloaded.');
    } catch (err) {
      console.error('ZIP export failed:', err);
      toast.error('Could not generate ZIP file.');
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-sm flex flex-col">
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <img src="/ks-logo.png" alt="K's" className="h-6 w-6 object-contain" />
          <h2 className="text-lg font-semibold text-primary">Sheet Preview</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showVisibility ? 'default' : 'outline'}
            onClick={() => setShowVisibility(!showVisibility)}
            className="h-8"
            data-testid="cad-visibility-toggle"
          >
            <Settings2 className="h-3 w-3 mr-1" />Visibility
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" disabled={exporting} className="h-8 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cad-export-btn">
                {exporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}Export<ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDownloadConsolidated}><Image className="h-4 w-4 mr-2" />Consolidated PNG</DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadZip}><Package className="h-4 w-4 mr-2" />Views + Consolidated (ZIP)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Visibility Controls Panel */}
      {showVisibility && (
        <div className="px-6 py-3 border-b border-border bg-muted/30 flex flex-wrap items-center gap-4" data-testid="cad-visibility-panel">
          <span className="text-xs font-semibold text-muted-foreground mr-2">Show in output:</span>
          {VISIBILITY_OPTIONS.map(opt => (
            <label key={opt.key} className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={visibility[opt.key]}
                onCheckedChange={() => toggleVisibility(opt.key)}
                data-testid={`cad-vis-${opt.key}`}
              />
              <span className="text-xs text-foreground">{opt.label}</span>
            </label>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto flex justify-center p-8 bg-muted/30">
        <div ref={previewRef} style={{ width: '800px', padding: '0', backgroundColor: '#ffffff', fontFamily: 'Arial, Helvetica, sans-serif', color: '#000000', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', alignSelf: 'flex-start', border: '2px solid #000' }}>
          {/* Header — full width */}
          {visibility.header && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 16px', borderBottom: '2px solid #000' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#000' }}>Style No: {data.styleNo || '\u2014'}</div>
                <div style={{ fontSize: '12px', color: '#444', marginTop: '2px' }}>{data.jewelryType}{data.size ? ` | Size: ${data.size}` : ''}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {data.customerDesignNo && <div style={{ fontSize: '13px', fontWeight: 700, color: '#000' }}>Customer No: {data.customerDesignNo}</div>}
                <div style={{ fontSize: '12px', color: '#444' }}>Date: {data.date}</div>
                {data.designerName && <div style={{ fontSize: '13px', fontWeight: 700, color: '#000' }}>#{data.designerName}</div>}
              </div>
            </div>
          )}
          {/* Two-column layout: Views LEFT, Data RIGHT */}
          <div style={{ display: 'flex', borderBottom: '1px solid #ddd' }}>
            {/* LEFT — Image Views */}
            {visibility.images && (
              <div style={{ width: '55%', padding: '8px', borderRight: '1px solid #ddd' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                  {data.images.map((img, i) => (
                    <div key={i} style={{ position: 'relative', border: '1px solid #ccc', aspectRatio: '1', backgroundColor: '#f9f9f9', overflow: 'hidden' }}>
                      {img.url ? (
                        <>
                          <img src={img.url} alt={img.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          {(img.highlights || []).length > 0 && (
                            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }} viewBox="0 0 100 100" preserveAspectRatio="none">
                              {(img.highlights || []).map(hl => (<path key={hl.id} d={hl.points.map((p, j) => `${j === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')} fill="none" stroke={hl.color} strokeWidth={hl.strokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={hl.opacity} />))}
                            </svg>
                          )}
                          {img.annotations.map(ann => {
                            const style = ann.style || DEFAULT_ANNOTATION_STYLE;
                            const dg = ann.dimensionGeometry;
                            const lc = style.lineColor || '#000';
                            const textX = (ann.textOffsetX !== undefined) ? (ann.x1+ann.x2)/2+ann.textOffsetX : (ann.x1+ann.x2)/2;
                            const textY = (ann.textOffsetY !== undefined) ? (ann.y1+ann.y2)/2+ann.textOffsetY : (ann.y1+ann.y2)/2-1.5;
                            const refX = dg ? (dg.dimStart.x+dg.dimEnd.x)/2 : (ann.x1+ann.x2)/2;
                            const refY = dg ? (dg.dimStart.y+dg.dimEnd.y)/2 : (ann.y1+ann.y2)/2;
                            const lineEls = [];
                            if (dg) {
                              lineEls.push(<HtmlLine key={`ext1-${ann.id}`} x1={dg.ext1Start.x} y1={dg.ext1Start.y} x2={dg.ext1End.x} y2={dg.ext1End.y} color={lc} width={2} dashed opacity={0.85} />);
                              lineEls.push(<HtmlLine key={`ext2-${ann.id}`} x1={dg.ext2Start.x} y1={dg.ext2Start.y} x2={dg.ext2End.x} y2={dg.ext2End.y} color={lc} width={2} dashed opacity={0.85} />);
                              lineEls.push(<HtmlLine key={`dim-${ann.id}`} x1={dg.dimStart.x} y1={dg.dimStart.y} x2={dg.dimEnd.x} y2={dg.dimEnd.y} color={lc} width={2} />);
                              const dx=dg.dimEnd.x-dg.dimStart.x, dy=dg.dimEnd.y-dg.dimStart.y, len=Math.sqrt(dx*dx+dy*dy);
                              if (len > 0.5) { const nx=-dy/len*2.5, ny=dx/len*2.5;
                                lineEls.push(<HtmlLine key={`t1-${ann.id}`} x1={dg.dimStart.x-nx} y1={dg.dimStart.y-ny} x2={dg.dimStart.x+nx} y2={dg.dimStart.y+ny} color={lc} width={2} />);
                                lineEls.push(<HtmlLine key={`t2-${ann.id}`} x1={dg.dimEnd.x-nx} y1={dg.dimEnd.y-ny} x2={dg.dimEnd.x+nx} y2={dg.dimEnd.y+ny} color={lc} width={2} />);
                              }
                            } else {
                              lineEls.push(<HtmlLine key={`line-${ann.id}`} x1={ann.x1} y1={ann.y1} x2={ann.x2} y2={ann.y2} color={lc} width={2} />);
                              const dx=ann.x2-ann.x1, dy=ann.y2-ann.y1, len=Math.sqrt(dx*dx+dy*dy);
                              if (len > 0.5) { const nx=-dy/len*2.5, ny=dx/len*2.5;
                                lineEls.push(<HtmlLine key={`t1-${ann.id}`} x1={ann.x1-nx} y1={ann.y1-ny} x2={ann.x1+nx} y2={ann.y1+ny} color={lc} width={2} />);
                                lineEls.push(<HtmlLine key={`t2-${ann.id}`} x1={ann.x2-nx} y1={ann.y2-ny} x2={ann.x2+nx} y2={ann.y2+ny} color={lc} width={2} />);
                              }
                            }
                            if (Math.abs(textX-refX) > 1 || Math.abs(textY-refY) > 1) {
                              lineEls.push(<HtmlLine key={`leader-${ann.id}`} x1={refX} y1={refY} x2={textX} y2={textY} color={lc} width={1.5} dashed opacity={0.7} />);
                            }
                            return <div key={`lines-${ann.id}`}>{lineEls}</div>;
                          })}
                          {img.annotations.map(ann => {
                            const style = ann.style || DEFAULT_ANNOTATION_STYLE;
                            const textX = (ann.textOffsetX !== undefined) ? (ann.x1+ann.x2)/2+ann.textOffsetX : (ann.x1+ann.x2)/2;
                            const textY = (ann.textOffsetY !== undefined) ? (ann.y1+ann.y2)/2+ann.textOffsetY : (ann.y1+ann.y2)/2-1.5;
                            return (
                              <div key={`pv-text-${ann.id}`} style={{ position: 'absolute', left: `${textX}%`, top: `${textY}%`, transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 10 }}>
                                <span style={{ display: 'inline-block', padding: '1px 3px', borderRadius: '2px', whiteSpace: 'nowrap', fontSize: `clamp(7px, ${(style.fontSize || 4.5) * 0.2}rem, 14px)`, fontWeight: 'bold', fontFamily: 'Arial, sans-serif', color: style.textColor || '#000', backgroundColor: 'rgba(255,255,255,0.9)', border: '1px solid rgba(0,0,0,0.15)', lineHeight: 1.2 }}>{ann.value}</span>
                              </div>
                            );
                          })}
                          {(img.textBoxes || []).map(tb => {
                            const style = tb.style || DEFAULT_TEXTBOX_STYLE;
                            return (
                              <div key={`pv-tb-${tb.id}`} style={{ position: 'absolute', left: `${tb.x}%`, top: `${tb.y}%`, transform: 'translate(0, -50%)', pointerEvents: 'none', zIndex: 10 }}>
                                <span style={{ display: 'inline-block', whiteSpace: 'nowrap', fontSize: `clamp(7px, ${(style.fontSize || 4.5) * 0.2}rem, 14px)`, fontWeight: style.bold ? 'bold' : 'normal', fontFamily: 'Arial, sans-serif', color: style.color || '#000', lineHeight: 1.2 }}>{tb.value}</span>
                              </div>
                            );
                          })}
                        </>
                      ) : (<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#aaa' }}>{img.label}</div>)}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1px 4px', fontSize: '9px', fontWeight: 600, backgroundColor: 'rgba(255,255,255,0.85)', color: '#333', textAlign: 'center' }}>{img.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* RIGHT — All Data */}
            <div style={{ width: visibility.images ? '45%' : '100%', padding: '8px 12px', fontSize: '12px' }}>
              {/* Metal & Weights */}
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Metal & Weight</div>
                <div style={{ color: '#333' }}>{data.metals.map(m => `${m.karat} ${m.color}${m.netWeight ? ` (${m.netWeight}g)` : ''}`).join(' / ')}</div>
                {(data.netWeight || data.grossWeight) && <div style={{ color: '#555', marginTop: '2px' }}>{data.netWeight && `Net: ${data.netWeight}g`}{data.netWeight && data.grossWeight && ' | '}{data.grossWeight && `Gross: ${data.grossWeight}g`}</div>}
              </div>
              {/* Stone Details */}
              {visibility.stones && data.stones.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '4px' }}>Stones <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>({totalStoneQty} pcs, {totalStoneWeight.toFixed(2)} ct)</span></div>
                  <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '1px solid #999' }}>{['Stone', 'Own.', 'Cut', 'Cat.', 'Qlty', 'Size', 'Ct.Wt', 'Qty', 'Tot'].map(h => (<th key={h} style={{ textAlign: 'left', padding: '2px 3px', fontSize: '9px', fontWeight: 700 }}>{h}</th>))}</tr></thead>
                    <tbody>{data.stones.map(s => (<tr key={s.id} style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '2px 3px' }}>{s.stone_category || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.ownership || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.stone_shape || s.type || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.category ? s.category.replace(' Stone','')[0] : '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.stone_quality || s.quality || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.size_mm || s.size || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.weight_per_stone || s.weight || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{s.quantity || '\u2014'}</td><td style={{ padding: '2px 3px' }}>{((parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0)).toFixed(2)}</td></tr>))}</tbody>
                  </table>
                </div>
              )}
              {/* Findings */}
              {visibility.findings && data.findings.length > 0 && (
                <div style={{ marginBottom: '6px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '3px' }}>Findings</div>
                  {data.findings.filter(f => f.type).map((f, i) => (<div key={f.id} style={{ color: '#333', fontSize: '11px' }}>{f.type}{f.weight && ` \u2014 ${f.weight}g`}{f.details && ` (${f.details})`}</div>))}
                </div>
              )}
              {/* Finishing */}
              {visibility.finishing && data.finishing && (
                <div style={{ marginBottom: '6px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Finishing: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.finishing}</span></div>
              )}
              {/* Engraving */}
              {visibility.engraving && data.engravingInstructions && (
                <div style={{ marginBottom: '6px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Engraving: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.engravingInstructions}</span></div>
              )}
              {visibility.engraving && data.engravingImage?.url && (
                <div style={{ marginBottom: '6px' }}><img src={data.engravingImage.url} alt="Engraving ref" style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', border: '1px solid #ccc' }} /></div>
              )}
              {/* Customer Reference */}
              {visibility.customerImages && data.customerImages.length > 0 && (
                <div style={{ marginBottom: '6px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '3px' }}>Customer Reference</div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{data.customerImages.map((img, i) => (<img key={i} src={img.url} alt={`Ref ${i + 1}`} style={{ maxWidth: '80px', maxHeight: '60px', objectFit: 'contain', border: '1px solid #ccc' }} />))}</div>
                </div>
              )}
              {/* Notes */}
              {visibility.notes && data.otherDetails && (
                <div style={{ marginBottom: '4px' }}><span style={{ fontWeight: 'bold', fontSize: '11px' }}>Notes: </span><span style={{ fontSize: '11px', color: '#333' }}>{data.otherDetails}</span></div>
              )}
              <div style={{ fontSize: '9px', color: '#888', fontStyle: 'italic', marginTop: '4px' }}>* Net & gross weights do not include suggested findings weight.</div>
            </div>
          </div>
          {/* Footer */}
          <div style={{ padding: '4px 16px', textAlign: 'center', fontSize: '10px', color: '#999' }}>K's Jewelry &mdash; CAD Sheet</div>
        </div>
      </div>
    </div>
  );
}
